/**
 * SSE frame parser shared between the d-orca backend (where it parses
 * upstream Trusstor SSE chunks) and the frontend (where it parses the
 * d-orca proxy's own SSE response). Identical logic at both boundaries
 * — kept in one place so a fix on either side doesn't drift.
 *
 * Per the SSE spec (https://html.spec.whatwg.org/multipage/server-sent-events.html#parsing-an-event-stream):
 *   - frames are delimited by a blank line
 *   - each non-blank line is a `field:value` pair
 *   - lines starting with `:` are comments
 *   - the spec allows `\n`, `\r`, or `\r\n` as line terminators; the
 *     buffer is expected to be CRLF-normalised before the splitter
 *     runs (callers do `.replace(/\r\n/g, '\n')`).
 *
 * Returns `null` when the frame contains no `data:` lines (heartbeat
 * comments, malformed frames). Callers should treat null as "skip" and
 * keep reading the next frame.
 */
export interface SseFrame {
  event: string;
  data: string;
}

export function parseSseFrame(raw: string): SseFrame | null {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of raw.split('\n')) {
    if (!line || line.startsWith(':')) continue;
    const sep = line.indexOf(':');
    if (sep === -1) continue;
    const field = line.slice(0, sep).trim();
    // SSE spec: optional single space after the colon.
    const value = line.slice(sep + 1).replace(/^ /, '');
    if (field === 'event') event = value;
    else if (field === 'data') dataLines.push(value);
  }
  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join('\n') };
}
